import { useMutation, useQuery } from '@tanstack/react-query';
import { Alert, Button, Center, Container, Group, Loader, Paper, Stack, Text, ThemeIcon, Title } from '@mantine/core';
import { IconAlertCircle, IconCheck, IconX } from '@tabler/icons-react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { clearSessionExpiry, logoutSession } from '../api/auth';
import { fetchEnrollmentProductResult, fetchSubmissionResult } from '../api/result';
import { ApiError } from '../api/http';
import PageShell from '../components/PageShell';
import { requestDraftRestore } from '../hooks/useFormDraft';
import { useProtectedSession } from '../hooks/useProtectedSession';
import { useResolvedProgram } from '../hooks/useResolvedProgram';
import { buildReturnToPath } from '../utils/portalPath';
import { buildEnrollmentFormPath, resolveEnrollmentProduct } from '../utils/enrollmentProduct';

const CARD_INFO_FALLBACK = 'Not available';

function normalizeCardDetail(value?: string): string | null {
  const normalizedValue = value?.trim();

  return normalizedValue ? normalizedValue : null;
}

function capitalizeMessage(message: string): string {
  for (let index = 0; index < message.length; index += 1) {
    const char = message[index];
    if (/[a-z]/.test(char)) {
      return `${message.slice(0, index)}${char.toUpperCase()}${message.slice(index + 1)}`;
    }
    if (/[A-Z0-9]/.test(char)) {
      return message;
    }
  }

  return message;
}

interface SubmissionResultPageProps {
  siteType: 'enrollments' | 'escalations';
  // Overrides the :resultId route param when rendered from a router with a different param name.
  resultId?: string;
  product?: string;
}

export default function SubmissionResultPage({ siteType, resultId: resultIdProp, product }: SubmissionResultPageProps) {
  const params = useParams<{ resultId: string }>();
  const resultId = resultIdProp ?? params.resultId;
  const { data: config, isLoading, error, resolvedProgram, sessionScopeKey } = useResolvedProgram();
  const location = useLocation();
  const navigate = useNavigate();
  const sessionQuery = useProtectedSession(sessionScopeKey);
  const logoutMutation = useMutation({
    mutationFn: logoutSession,
  });
  const resultQuery = useQuery({
    queryKey: ['result', siteType, product, resultId, resolvedProgram?.clientName, resolvedProgram?.programName],
    queryFn: () => product
      ? fetchEnrollmentProductResult(product, resultId ?? '')
      : fetchSubmissionResult(siteType, resultId ?? ''),
    enabled: Boolean(resultId && config && (!product || resolveEnrollmentProduct(config, product)) && !sessionQuery.isUnauthorized),
    retry: false,
  });
  if (!resultId) {
    return <Navigate to="/" replace />;
  }

  if (isLoading || sessionQuery.isLoading || resultQuery.isLoading) {
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (sessionQuery.isUnauthorized) {
    return <Navigate to="/auth/request" state={{ returnTo: buildReturnToPath(location) }} replace />;
  }

  if (sessionQuery.isError) {
    return (
      <Container size="sm" pt="xl">
        <Alert color="red" title="Session Error">
          Unable to verify your session. Please try refreshing the page.
        </Alert>
      </Container>
    );
  }

  if (error || !config) {
    return <Navigate to="/" replace />;
  }

  if (!resolvedProgram) {
    return <Navigate to="/" replace />;
  }

  const productConfig = resolveEnrollmentProduct(config, product);
  if (product && !productConfig) {
    return <Navigate to="/" replace />;
  }
  const effectiveConfig = productConfig?.effectiveConfig ?? config;
  const pages = productConfig?.pages ?? config[siteType]?.pages;

  if (resultQuery.isError) {
    const status = resultQuery.error instanceof ApiError ? resultQuery.error.status : undefined;
    const message = status === 404
      ? 'This result could not be found for the current microsite path.'
      : status === 503
        ? 'Result retrieval is currently unavailable. Please try again shortly.'
        : 'Unable to load this result. It may no longer be available for this microsite path.';

    return (
      <Container size="sm" pt="xl">
        <Alert color="red" title="Result Error">
          {message}
        </Alert>
      </Container>
    );
  }

  const draftScopeKey = product
    ? `${resolvedProgram.draftScopeKeys[siteType]}:${product}`
    : resolvedProgram.draftScopeKeys[siteType];
  const isEscalation = siteType === 'escalations';
  const sessionEmail = sessionQuery.data?.email;

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } finally {
      if (sessionScopeKey) {
        clearSessionExpiry(sessionScopeKey);
      }
      navigate('/auth/request', { replace: true });
    }
  };

  const record = resultQuery.data;
  const outcome = record?.outcome;
  const isSuccess = outcome === 'success';
  const isAlreadyEnrolled = outcome === 'already_enrolled';
  const isIneligible = outcome === 'ineligible';
  const isRejected = outcome === 'rejected';
  const outcomePageConfig = isSuccess
    ? pages?.success
    : isAlreadyEnrolled
      ? pages?.alreadyEnrolled ?? pages?.success
    : isIneligible
      ? pages?.ineligible
      : isRejected
        ? pages?.rejected
        : undefined;
  const title = outcomePageConfig?.title ?? (
    isAlreadyEnrolled
      ? 'Patient Already Enrolled'
      : isSuccess
        ? (isEscalation ? 'Escalation Submitted Successfully' : 'Enrollment Submitted Successfully')
      : isIneligible
        ? 'Patient Not Eligible'
        : isRejected
          ? (isEscalation ? 'Escalation Could Not Be Processed' : 'Enrollment Could Not Be Processed')
          : (isEscalation ? 'Submission Error' : 'Unable to Complete Enrollment')
  );
  const color = (isSuccess || isAlreadyEnrolled) ? 'green' : 'red';
  const defaultMessage = isAlreadyEnrolled
    ? 'This patient was already enrolled. The existing copay card information is shown below.'
    : isSuccess
      ? (isEscalation ? 'The escalation request was submitted successfully.' : 'The enrollment was submitted successfully.')
    : isIneligible
      ? `The patient does not meet the eligibility criteria for the ${effectiveConfig.theme.displayName}.`
      : isRejected
        ? (isEscalation ? 'The escalation request could not be processed. Please try again or contact support.' : 'The enrollment could not be processed. Please try again or contact support.')
        : (isEscalation ? 'The escalation request could not be completed. Please try again.' : 'The enrollment could not be completed. Please try again.');
  const configuredMessage = outcomePageConfig?.message;
  const message = capitalizeMessage(configuredMessage ?? defaultMessage);
  const rawMessages = record?.details?.messages;
  const recordMessages = Array.isArray(rawMessages)
    ? rawMessages.filter((detail: unknown): detail is string => typeof detail === 'string')
    : [];
  const recordFieldErrors = record?.details?.fieldErrors && typeof record.details.fieldErrors === 'object'
    ? Object.entries(record.details.fieldErrors as Record<string, string>).map(([field, detail]) => `${field}: ${detail}`)
    : [];
  const details = [
    ...recordMessages,
    ...recordFieldErrors,
  ]
    .filter((detail, index, values) => detail && values.indexOf(detail) === index)
    .filter((detail) => detail !== configuredMessage)
    .map((detail) => capitalizeMessage(detail));
  const icon = (isSuccess || isAlreadyEnrolled) ? <IconCheck size={20} /> : <IconX size={20} />;
  const titlePrefix = isEscalation ? 'Escalation Request Result' : 'Submission Status';
  const cardDetails = [
    { label: 'Card ID', value: normalizeCardDetail(record?.details?.cardNumber as string | undefined) },
    { label: 'BIN', value: normalizeCardDetail(record?.details?.cardBin as string | undefined) },
    { label: 'PCN', value: normalizeCardDetail(record?.details?.cardPcn as string | undefined) },
    { label: 'Group', value: normalizeCardDetail(record?.details?.cardGroup as string | undefined) },
  ];
  const hasCardDetails = cardDetails.some((detail) => detail.value);
  const escalationReference = (record?.details?.beniqTransactionId as string | undefined)
    ?? record?.referenceIds?.beniqTransactionId;
  const showLegacyTransactionId = isSuccess
    && isEscalation
    && config.solution !== 'escalationiq'
    && Boolean(escalationReference);

  return (
    <PageShell
      config={effectiveConfig}
      sessionEmail={sessionEmail}
      onGoHome={() => navigate('/')}
      onLogout={() => { void handleLogout(); }}
      isLoggingOut={logoutMutation.isPending}
      support={outcomePageConfig?.showSupportInfo === true ? effectiveConfig.support : undefined}
    >
      <Paper p="xl" radius="md" withBorder className="portal-panel">
          <Stack gap="md">
            <Text fw={600} c="dimmed">{titlePrefix}</Text>
            <Group gap="sm" align="center">
              <ThemeIcon color={color} size="lg" radius="xl" variant="light">
                {icon}
              </ThemeIcon>
              <Title order={3}>{title}</Title>
            </Group>
            <Text>{message}</Text>
            {showLegacyTransactionId && (
              <Group gap="xs" align="flex-start">
                <IconAlertCircle size={16} color="var(--mantine-color-gray-6)" style={{ marginTop: 2 }} />
                <Text size="md" fw={700}>
                  BenefitIQ Transaction ID: {escalationReference}
                </Text>
              </Group>
            )}
            {details.length > 0 && (
              <Alert color="yellow" variant="light" p="sm">
                <Stack gap={4}>
                  {details.map((detail) => (
                    <Text key={detail} size="sm">{detail}</Text>
                  ))}
                </Stack>
              </Alert>
            )}
            {(isSuccess || isAlreadyEnrolled) && !isEscalation && hasCardDetails && (
              <Paper
                p="md"
                radius="md"
                withBorder
                className="portal-panel portal-panel-muted"
                style={{
                  width: '100%',
                  maxWidth: 360,
                  alignSelf: 'center',
                }}
              >
                <Stack gap="sm">
                  <Group justify="space-between" align="flex-start" wrap="nowrap">
                    <Text fw={700}>Copay Card Information</Text>
                    <ThemeIcon color="green" variant="light" radius="xl" size="lg">
                      <IconCheck size={18} />
                    </ThemeIcon>
                  </Group>
                  <Stack gap="xs">
                    {cardDetails.map((detail) => (
                      <Group key={detail.label} gap="sm" align="baseline" wrap="nowrap">
                        <Text size="sm" c="dimmed" ta="right" style={{ width: 72, flexShrink: 0 }}>
                          {detail.label}:
                        </Text>
                        <Text
                          size="sm"
                          fw={detail.value ? 600 : 500}
                          ta="left"
                          c={detail.value ? undefined : 'dimmed'}
                          style={{ flex: 1 }}
                        >
                          {detail.value ?? CARD_INFO_FALLBACK}
                        </Text>
                      </Group>
                    ))}
                  </Stack>
                </Stack>
              </Paper>
            )}
            {showLegacyTransactionId && (
              <Alert color="blue" variant="light" title="What's Next?">
                <Text size="sm">
                  Your escalation request has been submitted and is being processed. You can bookmark this page for your records. If you need to reference this escalation in the future, provide the above Transaction ID.
                </Text>
              </Alert>
            )}
            {isSuccess && isEscalation && config.solution === 'escalationiq' && resultId && (
              <Button
                variant="light"
                size="sm"
                onClick={() => navigate(`/escalations/history/${resultId}`)}
              >
                Track escalation status →
              </Button>
            )}
          </Stack>
        </Paper>
          {isSuccess || isAlreadyEnrolled || isIneligible ? (
            <Button
              fullWidth
              onClick={() => navigate(isEscalation ? '/escalations' : buildEnrollmentFormPath(product), { replace: true })}
              className="portal-primary-button"
            >
              {isIneligible ? 'Start Over' : (isEscalation ? 'Submit Another Escalation' : 'Submit Another Enrollment')}
            </Button>
          ) : (
            <Button
              fullWidth
              onClick={() => {
                requestDraftRestore(draftScopeKey);
                navigate(isEscalation ? '/escalations' : buildEnrollmentFormPath(product), { state: { restoreDraft: true } });
              }}
              className="portal-primary-button"
            >
              Back to form with saved data
            </Button>
          )}
    </PageShell>
  );
}